/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { audioApi } from '../../../../api';
import { createAudio, destroyAudio } from '../../../../helpers/create-audio';
import { decodeDurationMs } from '../../../../helpers/decode-duration';
import {
  ListenPill,
  ListenPillText,
  ListenPillIcon,
  PlayedWrapper,
  PlayedAudio,
  ProgressText,
  ProgressBar,
  ProgressTrack,
  ProgressBarWrapper,
  AudioControlButton,
} from '../styles/question';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const AudioSpinner = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(255, 255, 255, 0.15);
  border-top-color: #5e84ed;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

interface AudioPlayerProps {
  audioId?: string;
  audioUrl?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioId,
  audioUrl,
  onPlay,
  onPause,
  onEnded,
}) => {
  // Audio playback state
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressBarWrapperRef = useRef<HTMLDivElement | null>(null);
  const lastSeekedTimeRef = useRef<number>(0);
  const isDraggingRef = useRef(false);
  const onEndedRef = useRef(onEnded);
  const decodedDurationRef = useRef<number>(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);

  // Keep refs in sync to avoid stale closures in audio event listeners
  useEffect(() => { onEndedRef.current = onEnded; }, [onEnded]);
  useEffect(() => { isDraggingRef.current = isDragging; }, [isDragging]);

  // Build the audio source URL
  // - If audioUrl is provided (static file), use it directly
  // - If audioId is provided (DB audio), fetch an opaque stream token
  const [sourceUrl, setSourceUrl] = useState<string | null>(audioUrl ?? null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (audioUrl) { setSourceUrl(audioUrl); return; }
    if (!audioId) { setSourceUrl(null); return; }

    let cancelled = false;
    audioApi.getStreamUrl(audioId).then((url) => {
      if (!cancelled) setSourceUrl(url);
    }).catch((err) => {
      console.error('Failed to get stream URL:', err);
      if (!cancelled) setSourceUrl(null);
    });
    return () => { cancelled = true; };
  }, [audioId, audioUrl]);

  // Fetch audio as blob and decode exact duration before creating the audio element.
  // WebM/Opus streams report Infinity duration until nearly fully buffered —
  // decodeAudioData gives us the real value upfront.
  const fetchAudioBlob = useCallback(async (url: string, signal?: AbortSignal) => {
    setIsAudioLoading(true);
    const MAX_RETRIES = 3;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const res = await fetch(url, signal ? { signal } : undefined);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (signal?.aborted) return;

        const ms = await decodeDurationMs(blob);
        if (signal?.aborted) return;

        const objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);

        if (ms > 0) {
          decodedDurationRef.current = ms / 1000;
          setDuration(ms / 1000);
        }
        setIsAudioLoading(false);
        return;
      } catch {
        if (signal?.aborted) return;
        if (attempt < MAX_RETRIES - 1) {
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }
        // Final fallback: use stream URL directly (duration may be Infinity)
        setBlobUrl(url);
      }
    }
    if (!signal?.aborted) setIsAudioLoading(false);
  }, []);

  useEffect(() => {
    if (!sourceUrl) return;

    const controller = new AbortController();
    fetchAudioBlob(sourceUrl, controller.signal);

    return () => {
      controller.abort();
    };
  }, [sourceUrl, fetchAudioBlob]);

  // Initialize audio element once we have a blob URL
  useEffect(() => {
    const url = blobUrl;
    if (!url) return;

    // Tear down any previous audio element
    if (audioRef.current) {
      destroyAudio(audioRef.current);
      audioRef.current = null;
    }

    const audio = createAudio();
    audio.src = url;
    audioRef.current = audio;

    const updateDuration = () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
        decodedDurationRef.current = audio.duration;
      }
    };
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);

    audio.addEventListener('waiting', () => setIsBuffering(true));
    audio.addEventListener('playing', () => setIsBuffering(false));
    audio.addEventListener('canplay', () => setIsBuffering(false));

    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onEndedRef.current?.();

      // Animate collapse back to ListenPill
      setIsCollapsing(true);
      setTimeout(() => {
        setHasPlayed(false);
        setIsCollapsing(false);
      }, 300);
    });

    return () => {
      destroyAudio(audio);
      audioRef.current = null;
    };
  }, [blobUrl]);

  // RAF-based progress loop — smoother than relying on timeupdate events
  useEffect(() => {
    if (!isPlaying) return;

    let raf = 0;
    const tick = () => {
      const a = audioRef.current;
      if (a && !isDraggingRef.current) {
        const t = a.currentTime;
        if (isFinite(t) && t >= 0) {
          setCurrentTime(t);
        }

        // Try to pick up live duration if it becomes finite
        if (isFinite(a.duration) && a.duration > 0 && decodedDurationRef.current <= 0) {
          decodedDurationRef.current = a.duration;
          setDuration(a.duration);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);

  // Format time to MM:SS
  const formatTime = (time: number): string => {
    if (!isFinite(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    if (!audioRef.current || isExpanding) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      onPause?.();
    } else if (!hasPlayed) {
      // First play: transition from ListenPill to player
      setHasPlayed(true);
      setIsExpanding(true);

      // If audio has ended or hasn't started, restart
      if (currentTime >= duration - 0.5 || currentTime === 0) {
        audioRef.current.currentTime = 0;
        setCurrentTime(0);
      }

      audioRef.current.play();
      setIsPlaying(true);
      onPlay?.();

      setTimeout(() => {
        setIsExpanding(false);
      }, 300);
    } else {
      // Resume from paused state (player already visible)
      if (currentTime >= duration - 0.5) {
        audioRef.current.currentTime = 0;
        setCurrentTime(0);
      }

      audioRef.current.play();
      setIsPlaying(true);
      onPlay?.();
    }
  };

  // Seek to position based on click or drag
  const seekToPosition = useCallback((clientX: number): number | null => {
    if (!progressBarWrapperRef.current || !audioRef.current || !isFinite(duration) || duration <= 0) return null;

    const rect = progressBarWrapperRef.current.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * duration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    lastSeekedTimeRef.current = newTime;
    return newTime;
  }, [duration]);

  // Auto-play after seeking
  const autoPlayAfterSeek = useCallback((seekTime?: number) => {
    if (!audioRef.current) return;

    // Ensure we're at the correct time before playing
    if (seekTime !== undefined) {
      audioRef.current.currentTime = seekTime;
    }

    audioRef.current.play().then(() => {
      setIsPlaying(true);
      onPlay?.();
    }).catch((err) => {
      console.error('Auto-play failed:', err);
    });
  }, [onPlay]);

  // Handle click on progress bar — seek and auto-play
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const seekTime = seekToPosition(e.clientX);
    if (seekTime !== null) {
      setTimeout(() => autoPlayAfterSeek(seekTime), 50);
    }
  };

  // Handle mouse drag start
  const handleDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    seekToPosition(e.clientX);
  };

  // Handle touch drag start
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsDragging(true);
    seekToPosition(e.touches[0].clientX);
  };

  // Handle mouse & touch drag move + auto-play on release
  useEffect(() => {
    if (!isDragging) return;

    let lastSeekTime = lastSeekedTimeRef.current;

    const computeSeekTime = (clientX: number): number | null => {
      if (!progressBarWrapperRef.current || !isFinite(duration) || duration <= 0) return null;
      const rect = progressBarWrapperRef.current.getBoundingClientRect();
      const clickX = clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, clickX / rect.width));
      return percentage * duration;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!audioRef.current) return;
      const newTime = computeSeekTime(e.clientX);
      if (newTime === null) return;

      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      lastSeekTime = newTime;
      lastSeekedTimeRef.current = newTime;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!audioRef.current) return;
      const newTime = computeSeekTime(e.touches[0].clientX);
      if (newTime === null) return;

      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      lastSeekTime = newTime;
      lastSeekedTimeRef.current = newTime;
    };

    const handleRelease = () => {
      setIsDragging(false);

      if (!audioRef.current || !isFinite(lastSeekTime)) return;
      audioRef.current.currentTime = lastSeekTime;
      audioRef.current.play().then(() => {
        setIsPlaying(true);
        onPlay?.();
      }).catch((err) => {
        console.error('Auto-play failed:', err);
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleRelease);
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleRelease);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleRelease);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleRelease);
    };
  }, [isDragging, duration, onPlay]);

  // Calculate progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <>
      {!hasPlayed && isAudioLoading && (
        <ListenPill isLoading>
          <AudioSpinner style={{ width: 16, height: 16 }} />
          <ListenPillText>Listen</ListenPillText>
        </ListenPill>
      )}

      {!hasPlayed && !isAudioLoading && (
        <ListenPill onClick={togglePlayPause}>
          <ListenPillIcon
            src="/icons/onboarding/question/play-audio.svg"
            alt="Play Audio"
          />
          <ListenPillText>Listen</ListenPillText>
        </ListenPill>
      )}

      {hasPlayed && (
        <PlayedWrapper isAnimating={isExpanding} isCollapsing={isCollapsing}>
          <PlayedAudio>
            {isBuffering ? (
              <AudioSpinner />
            ) : (
              <AudioControlButton
                src={isPlaying
                  ? '/icons/onboarding/question/pause-audio.svg'
                  : '/icons/onboarding/question/play-audio.svg'
                }
                alt={isPlaying ? 'Pause Audio' : 'Play Audio'}
                onClick={togglePlayPause}
              />
            )}
            <ProgressBarWrapper
              ref={progressBarWrapperRef}
              onClick={handleProgressClick}
              onMouseDown={handleDragStart}
              onTouchStart={handleTouchStart}
            >
              <ProgressBar progress={progress} />
              <ProgressTrack progress={progress} />
            </ProgressBarWrapper>
            <ProgressText>{formatTime(currentTime)}</ProgressText>
          </PlayedAudio>
        </PlayedWrapper>
      )}
    </>
  );
};
